import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const {
  Question, SignIn, CloudArrowUp, MusicNotesPlus, Queue, Clock, CalendarDots,
  Megaphone, Storefront, Play, Users, CreditCard, Lock, Wrench,
  MagnifyingGlass, ListBullets, Link: LinkIcon, PlugsConnected, CaretRight,
  ArrowSquareOut, WarningCircle, CheckCircle, Info
} = PhosphorIcons;
const UsersIcon = Users;

/*
  In-app help centre.

  Structure:
    • Left column = sticky table-of-contents that scrolls with the page.
    • Right column = the article body — each section is an <article id="…">
      so the ToC anchors work and deep-links like /dashboard/help#dayparting
      drop the reader straight onto the relevant step.
    • A search input filters sections by matching title/body text. No section
      is hidden when search is empty — the default experience is "scroll it
      like a manual".

  Everything is static text — intentionally not pulling from an API so the
  help centre works even if the backend is unreachable.
*/

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: SignIn,
    description: 'Log in and complete the first-time setup.',
    body: () => (
      <>
        <Step n={1}>Go to your MusicSync URL and click <strong>Login</strong>.</Step>
        <Step n={2}>Keep the tab set to <strong>Tenant Login</strong> (default).</Step>
        <Step n={3}>Enter your email and password → click <strong>Login</strong>.</Step>
        <Callout icon={Info} tone="info">
          If another device is already signed in, you'll see <em>"You're already signed in elsewhere"</em>.
          Click <strong>End other session &amp; sign in here</strong> to evict the old device.
        </Callout>
        <Callout icon={WarningCircle} tone="warn">
          If this browser is signed in as a different user, log that user out first on the <code>/login</code> page.
          Only one active session is allowed per browser.
        </Callout>
        <Step n={4}>
          On your first login the <strong>Setup Wizard</strong> opens. Follow the 5 steps
          (Create Store → Upload/Import Music → Create Playlist → Assign Playlist → Open Kiosk)
          or click <strong>Skip for now</strong>. You can always return via the sidebar <em>Setup Guide</em>.
        </Step>
      </>
    ),
  },

  {
    id: 'google-drive-connect',
    title: 'Connect Google Drive',
    icon: CloudArrowUp,
    description: 'Stream your Drive music library directly to kiosks — no uploads needed.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Music Library</strong>.</Step>
        <Step n={2}>Click <strong>Connect Google Drive</strong>.</Step>
        <Step n={3}>In the Google sign-in popup, choose the account that holds your music.</Step>
        <Step n={4}>
          On the consent screen approve the scope
          (<em>"See, edit, create, and delete only specific Google Drive files you've opened or created with this app"</em>) → <strong>Continue</strong>.
        </Step>
        <Step n={5}>
          Back in MusicSync you'll see a <strong>Connected</strong> banner. Now click <strong>Pick from Drive</strong>.
        </Step>
        <Step n={6}>
          Google's own file picker opens in a popup. Browse your Drive, tick the tracks you want (multi-select works via
          Shift+click or Ctrl/Cmd+click), and click <strong>Select</strong>. The tracks appear in your library instantly
          with a <em>Drive</em> badge — no files are downloaded, MusicSync streams on demand.
        </Step>
        <Callout icon={Info} tone="info">
          <strong>Why Google's picker instead of MusicSync's own file browser?</strong> Google requires all third-party apps
          to use their narrow <code>drive.file</code> scope, which grants access only to files you explicitly pick.
          It's more privacy-preserving — MusicSync never sees anything else in your Drive.
        </Callout>
        <Callout icon={Info} tone="info">
          <strong>Token refreshes automatically.</strong> Your Drive access token expires every hour, but MusicSync refreshes it
          silently in the background, so music keeps streaming 24/7 without you reconnecting.
        </Callout>
        <Callout icon={WarningCircle} tone="warn">
          If a track later stops playing (rare — file was moved, renamed, or the per-file grant was revoked), click the
          <strong> Re-auth</strong> button on that track row in the Music Library. It opens the picker so you can point the
          track at the current file — playlists and schedules pointing to it keep working, no re-import churn.
        </Callout>
      </>
    ),
  },

  {
    id: 'music-library',
    title: 'Music library',
    icon: MusicNotesPlus,
    description: 'Your library of Drive-sourced tracks.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Music Library</strong>.</Step>
        <Step n={2}>
          Every track shown here streams directly from your connected Google Drive — MusicSync never downloads
          the audio file, it only stores a reference to it.
        </Step>
        <Step n={3}>
          Click <strong>Pick from Drive</strong> to open Google's Picker and add more tracks (see the previous section).
        </Step>
        <Step n={4}>
          The <strong>Re-auth</strong> button on each Drive track lets you re-attach it if the underlying file was moved,
          renamed, or the per-file grant was lost. Playlists and schedules pointing at the track keep working.
        </Step>
        <Callout icon={Info} tone="info">
          Deleting a track removes it from every playlist it's in. Empty playlists are auto-cleaned up.
        </Callout>
      </>
    ),
  },

  {
    id: 'playlists',
    title: 'Playlists',
    icon: Queue,
    description: 'Order your tracks into themed sets that can be scheduled.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Playlists</strong> → <strong>+ New Playlist</strong>.</Step>
        <Step n={2}>Give it a name (e.g. <em>"Afternoon Lounge"</em>), optional tags / description.</Step>
        <Step n={3}>Pick tracks from your library (Uploaded + Drive freely mixed) → <strong>Add to playlist</strong>.</Step>
        <Step n={4}>Drag to reorder. Tick <strong>Shuffle</strong> / <strong>Crossfade</strong> per playlist if needed.</Step>
        <Step n={5}>
          Choose a <strong>Track transition</strong>:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li><strong>Cut</strong> — the next track starts instantly when one ends. Simple, no gap.</li>
            <li><strong>Crossfade</strong> — the current track fades out while the next fades in over a duration you choose (0.5s–8.0s). Softer, radio-station-style transitions.</li>
          </ul>
        </Step>
        <Step n={6}>Click <strong>Save</strong>.</Step>
        <Callout icon={Info} tone="info">
          A playlist is just an ordered list of tracks — it becomes <em>active</em> only when a daypart or a store points at it.
        </Callout>
      </>
    ),
  },

  {
    id: 'dayparting',
    title: 'Dayparting / scheduling',
    icon: Clock,
    description: 'Play different playlists at different times of day, per store.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Dayparting</strong>.</Step>
        <Step n={2}>Pick the <strong>Store</strong> you want to schedule from the dropdown.</Step>
        <Step n={3}>Click <strong>+ Add Daypart</strong>.</Step>
        <Step n={4}>Fill in:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li><strong>Name</strong> — e.g. "Morning calm"</li>
            <li><strong>Start time</strong> / <strong>End time</strong> (tenant timezone)</li>
            <li><strong>Playlist</strong> — the one you created above</li>
            <li><strong>Days of week</strong> — mon/tue/… or all</li>
            <li><strong>Volume</strong>, <strong>Shuffle</strong>, <strong>Crossfade</strong> — optional overrides</li>
          </ul>
        </Step>
        <Step n={5}>Click <strong>Save</strong>. Add more dayparts to cover the whole day.</Step>
        <Callout icon={Info} tone="info">
          <strong>Timezone matters.</strong> Dayparts are evaluated in your tenant timezone set under
          <code className="mx-1">Settings → Branding</code>. Kiosks in other timezones still follow the tenant's clock.
        </Callout>
        <Callout icon={WarningCircle} tone="warn">
          If no daypart matches the current time, the kiosk shows <em>"No schedule configured"</em>. Make sure your dayparts cover all operating hours.
        </Callout>
      </>
    ),
  },

  {
    id: 'calendar',
    title: 'Calendar',
    icon: CalendarDots,
    description: 'Visualise all your dayparts across stores and days.',
    body: () => (
      <>
        <p>Sidebar → <strong>Calendar</strong>. A week-view shows every daypart on a grid. Drag to shift,
          click to edit, hover to preview the playlist. Useful for spotting gaps or overlaps at a glance.</p>
      </>
    ),
  },

  {
    id: 'announcements',
    title: 'Announcements',
    icon: Megaphone,
    description: 'Inject spoken messages (or promos) between songs at a cadence.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Announcements</strong> → <strong>+ New Announcement</strong>.</Step>
        <Step n={2}>Upload an audio file (the spoken message) or record one with your browser.</Step>
        <Step n={3}>
          Set <strong>Cadence</strong>:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li><strong>Every N minutes</strong> — plays roughly that often</li>
            <li><strong>Between each track</strong> — plays every song break</li>
            <li><strong>At specific times</strong> — e.g. 10:00, 13:00, 18:00</li>
          </ul>
        </Step>
        <Step n={4}>Pick <strong>Stores</strong> to target (all or selected).</Step>
        <Step n={5}>Set <strong>Active from / until</strong> dates if it's a limited-time campaign.</Step>
        <Step n={6}><strong>Save</strong>. Kiosks pick up the change within 30 seconds.</Step>
        <Callout icon={Info} tone="info">
          Announcements always fade the music down, play, then fade back — no abrupt cut-offs.
        </Callout>
      </>
    ),
  },

  {
    id: 'stores-kiosk',
    title: 'Stores & kiosks',
    icon: Storefront,
    description: 'Add stores, issue kiosk credentials, and get the in-store URL.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Stores</strong> → <strong>+ New Store</strong>.</Step>
        <Step n={2}>Name it (e.g. "Koramangala Outlet"), pick a group, set default volume.</Step>
        <Step n={3}>
          Create a <strong>Kiosk user</strong> for it: Sidebar → <strong>Team</strong> → <strong>+ New User</strong> →
          role = <strong>Kiosk</strong> → link to that store. Save the password — you'll type it at the tablet.
        </Step>
        <Step n={4}>
          Back on <strong>Stores</strong>, click <strong>Copy Kiosk URL</strong> (or scan the QR) for the store row.
        </Step>
        <Step n={5}>
          Open that URL on the in-store tablet / PC browser. Log in with the kiosk user credentials.
        </Step>
        <Step n={6}>
          For rolling out to many stores at once, use the <strong>Copy URLs</strong> button (tab-separated, paste-ready
          for WhatsApp / email) or <strong>Export URLs (CSV)</strong> from the top-right of the Stores page — it
          downloads a spreadsheet with every store's kiosk URL. Tick specific stores first to export only those.
        </Step>
      </>
    ),
  },

  {
    id: 'playing-on-kiosk',
    title: 'Playing music on the kiosk',
    icon: Play,
    description: 'First-time tap, location prompt, and what "normal" looks like.',
    body: () => (
      <>
        <Step n={1}>After the kiosk login, you'll see a dark player screen with <strong>Tap to Start</strong>.</Step>
        <Step n={2}>
          Tap it. This unlocks browser autoplay. You may also be asked for <strong>location permission</strong>
          — allow it so the store location appears on your dashboard (optional).
        </Step>
        <Step n={3}>
          Music starts immediately following the active daypart. You should see:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li>State badge reads <strong>PLAYING</strong></li>
            <li>Time advances (e.g. 0:42 / 3:18)</li>
            <li>Audio visualiser bars pulse</li>
            <li>Volume matches the dashboard slider</li>
          </ul>
        </Step>
        <Callout icon={Info} tone="info">
          <strong>Stall safety net:</strong> if audio stalls for 20s, the kiosk auto-skips and briefly shows
          <em>"Playback stalled"</em>. Normal resilience behaviour — not an error you need to act on.
        </Callout>
        <Callout icon={WarningCircle} tone="warn">
          <strong>Only one browser per kiosk.</strong> Opening the same kiosk URL in a second tab evicts the first
          with a <em>"Session closed"</em> overlay. Prevents double-playback and licensing issues.
        </Callout>
      </>
    ),
  },

  {
    id: 'remote-control',
    title: 'Remote control from the dashboard',
    icon: ListBullets,
    description: 'Play / pause / skip / volume — from anywhere, even on flaky Wi-Fi.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Dashboard</strong> (the store tiles).</Step>
        <Step n={2}>Each online store tile has <strong>Play / Pause / Skip / Prev</strong> buttons and a volume slider.</Step>
        <Step n={3}>Commands reach the kiosk over WebSocket, with a 3-second polling fallback — so they work even on mobile data / behind proxies.</Step>
        <Step n={4}>
          For a group of kiosks: pick the group in the filter → click <strong>Sync Group</strong> → choose the
          <em>source</em> kiosk in the dialog → <strong>Sync now</strong>. Every other kiosk jumps to the same track and plays.
        </Step>
      </>
    ),
  },

  {
    id: 'team',
    title: 'Team & permissions',
    icon: UsersIcon,
    description: 'Invite staff with scoped access (manager / kiosk).',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Team</strong> → <strong>+ New User</strong>.</Step>
        <Step n={2}>Pick a role:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
            <li><strong>Tenant admin</strong> — full control, only you should have this.</li>
            <li><strong>Manager</strong> — tick specific permissions (library, playlists, dayparting, calendar, announcements, analytics, legal). Restrict them to what they need.</li>
            <li><strong>Kiosk</strong> — login identity for the in-store tablet. Link to a specific store.</li>
          </ul>
        </Step>
        <Step n={3}>Email + temporary password → <strong>Create</strong>. They'll be prompted to change it on first login.</Step>
      </>
    ),
  },

  {
    id: 'subscription',
    title: 'Subscription & billing',
    icon: CreditCard,
    description: 'Plans, add-ons, renewals, and downgrades.',
    body: () => (
      <>
        <Step n={1}>Sidebar → <strong>Subscription</strong>.</Step>
        <Step n={2}>Your current plan shows <strong>Users</strong> + <strong>Locations</strong> quotas and next renewal date.</Step>
        <Step n={3}>
          <strong>Add users / locations mid-cycle</strong> — adds are pro-rated for the remaining days.
        </Step>
        <Step n={4}>
          <strong>Renewal choice</strong> — your current plan auto-carries to next cycle; change it any time before renewal.
        </Step>
        <Step n={5}>
          Payment via <strong>Razorpay</strong>. A 3-day countdown banner appears before expiry; we also email you 7 days before.
        </Step>
        <Callout icon={WarningCircle} tone="warn">
          <strong>Downgrade guard:</strong> you can't drop below your current used capacity (e.g. can't shrink to 2 users if 4 are active). Remove users / stores first, then downgrade.
        </Callout>
      </>
    ),
  },

  {
    id: 'session-security',
    title: 'Session & security',
    icon: Lock,
    description: 'How one-login-at-a-time works, and what the messages mean.',
    body: () => (
      <>
        <ul className="list-disc pl-6 space-y-2 text-sm">
          <li><strong>One device, one session.</strong> Logging in from a second device shows a prompt to end the first session.</li>
          <li><strong>One browser, one user.</strong> Same browser can't host two different user logins. Log the current user out first.</li>
          <li><strong>Cross-tab signout.</strong> Logging out in one tab signs out all other tabs instantly.</li>
          <li><strong>"You were signed out because this account logged in on another device"</strong> — someone (maybe you) signed in elsewhere. Log back in; if it wasn't you, change your password.</li>
          <li><strong>Session auto-expires after 24 hours.</strong> Re-login needed after that.</li>
          <li><strong>Kiosk sessions are exempt</strong> from the one-device rule — they're long-running by design.</li>
        </ul>
      </>
    ),
  },

  {
    id: 'disconnect-drive',
    title: 'Disconnect Google Drive',
    icon: PlugsConnected,
    description: 'Cleanly unlink Drive, delete streamed tracks, reset schedules.',
    body: () => (
      <>
        <Callout icon={WarningCircle} tone="warn">
          <strong>This is destructive for Drive-linked content:</strong> every Drive track in your library, every playlist entry pointing to them, and any resulting empty playlists are permanently deleted. Store schedules pointing to those playlists are reset. Uploaded (non-Drive) tracks are untouched.
        </Callout>
        <Step n={1}>Sidebar → <strong>Music Library</strong>.</Step>
        <Step n={2}>Click <strong>Disconnect Google Drive</strong>.</Step>
        <Step n={3}>
          A confirmation dialog tells you exactly how many tracks &amp; playlists will be removed. Review the numbers.
        </Step>
        <Step n={4}>Click <strong>Yes, disconnect</strong> (or <strong>Cancel</strong> to back out).</Step>
        <Step n={5}>
          Kiosks currently playing a Drive track immediately move on to the next available non-Drive track, or show <em>"No schedule configured"</em> if nothing else is scheduled.
        </Step>
        <Step n={6}>
          To reconnect later: repeat the <a href="#google-drive-connect" className="text-blue-600 underline">Connect Google Drive</a> steps. No history is preserved — treat it as a fresh connection.
        </Step>
      </>
    ),
  },

  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: Wrench,
    description: 'Common symptoms and the quickest fix.',
    body: () => (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#ede8dc] text-left">
                <th className="px-3 py-2 border border-[#d9d3c5] font-mono text-xs uppercase tracking-wider">Symptom</th>
                <th className="px-3 py-2 border border-[#d9d3c5] font-mono text-xs uppercase tracking-wider">Fix</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              <Tr symptom='Kiosk shows "Tap to Start" but nothing plays'>
                Tap it once. Browsers require an explicit gesture to unlock audio.
              </Tr>
              <Tr symptom="Dashboard buttons feel unresponsive">
                They're not dropped — a 3-second polling fallback will catch up. If persistent, check the kiosk's network.
              </Tr>
              <Tr symptom="Tracks skip after 1 hour of playback">
                Drive access tokens auto-refresh now (v19+). If it recurs, disconnect and reconnect Drive once; it resets the OAuth state.
              </Tr>
              <Tr symptom='"Session closed" overlay on the kiosk'>
                Another browser opened the same kiosk URL. Reload this tab to reclaim.
              </Tr>
              <Tr symptom='"You were signed out — logged in on another device"'>
                Normal. Log in again. If it wasn't you, change your password.
              </Tr>
              <Tr symptom='"No schedule configured" on the kiosk during operating hours'>
                No daypart covers the current time. Check <em>Dayparting</em> and fill the gap.
              </Tr>
              <Tr symptom="Announcement doesn't play">
                Check its <em>Active from/until</em> window and target-store list. Kiosks refresh announcements every 30s — wait that long.
              </Tr>
              <Tr symptom="Can't downgrade plan">
                You've exceeded the target plan's quota. Remove users/stores first, then retry.
              </Tr>
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-sm text-[#6b665c]">
          Still stuck? Email{' '}
          <a href="mailto:admin@musicsync.net" className="text-blue-600 hover:text-blue-700 underline">admin@musicsync.net</a>
          {' '}— include your tenant name, the store / kiosk URL, and a screenshot if possible.
        </p>
      </>
    ),
  },
];

// ── Small presentational helpers ────────────────────────────────────────
const Step = ({ n, children }) => (
  <div className="flex gap-3 mb-3" data-testid={`help-step-${n}`}>
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#7c9082] text-white text-xs font-mono flex items-center justify-center mt-0.5">
      {n}
    </div>
    <div className="flex-1 text-sm text-[#2d2a26] leading-relaxed">{children}</div>
  </div>
);

const Callout = ({ icon: Icon, tone, children }) => {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900',
    ok: 'bg-green-50 border-green-200 text-green-900',
  }[tone] || 'bg-[#f5f0e8] border-[#d9d3c5] text-[#2d2a26]';
  return (
    <div className={`my-4 flex items-start gap-3 rounded-lg border p-3 text-sm ${styles}`}>
      <Icon size={18} weight="bold" className="mt-0.5 flex-shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const Tr = ({ symptom, children }) => (
  <tr className="align-top">
    <td className="px-3 py-2 border border-[#d9d3c5] font-medium text-[#2d2a26]">{symptom}</td>
    <td className="px-3 py-2 border border-[#d9d3c5] text-[#4a453d]">{children}</td>
  </tr>
);

// ── Main page ──────────────────────────────────────────────────────────
const Help = () => {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const scrollRootRef = useRef(null);

  // Filter sections for search. We match title + description case-insensitively.
  // When no query, show everything — the ToC scroll behaviour handles navigation.
  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.id.includes(q)
    );
  }, [query]);

  // IntersectionObserver highlights the ToC entry whose article is on screen.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { root: null, rootMargin: '-80px 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [filteredSections]);

  // Jump-to-anchor honouring a fixed header by scroll-padding-top on <html>.
  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.history.replaceState(null, '', `#${id}`);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // On mount, if the URL has a hash, scroll to it after first paint.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (hash) {
      const t = setTimeout(() => jumpTo(hash), 100);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="space-y-6" ref={scrollRootRef} data-testid="help-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#7c9082] flex items-center justify-center">
            <Question size={20} weight="bold" className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#2d2a26] tracking-tight" data-testid="help-title">Help Centre</h1>
            <p className="text-xs text-[#6b665c] font-mono tracking-wider uppercase">
              Step-by-step guides for every MusicSync feature
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8477]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help… e.g. dayparting, disconnect drive, announcements"
          className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-[#d9d3c5] rounded-lg text-[#2d2a26] focus:outline-none focus:border-[#7c9082] font-mono"
          data-testid="help-search-input"
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        {/* ToC */}
        <aside className="order-2 lg:order-1">
          <nav className="lg:sticky lg:top-4 space-y-1" data-testid="help-toc">
            <p className="text-[10px] font-mono text-[#8a8477] uppercase tracking-wider mb-2 px-2">Sections</p>
            {filteredSections.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  onClick={() => jumpTo(s.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-xs font-mono transition-all ${
                    isActive
                      ? 'bg-[#e4ebe5] text-[#2d2a26] border-l-2 border-[#7c9082]'
                      : 'text-[#6b665c] hover:bg-[#ede8dc] border-l-2 border-transparent'
                  }`}
                  data-testid={`help-toc-${s.id}`}
                >
                  <Icon size={14} className="flex-shrink-0" />
                  <span className="truncate">{s.title}</span>
                </button>
              );
            })}
            {filteredSections.length === 0 && (
              <p className="px-2 py-4 text-xs text-[#8a8477] font-mono">No matching sections.</p>
            )}
          </nav>
        </aside>

        {/* Articles */}
        <div className="order-1 lg:order-2 space-y-8">
          {filteredSections.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                id={s.id}
                className="bg-white border border-[#d9d3c5] rounded-xl p-6 shadow-sm scroll-mt-24"
                data-testid={`help-section-${s.id}`}
              >
                <header className="flex items-start gap-3 mb-4 pb-4 border-b border-[#ede8dc]">
                  <div className="w-9 h-9 rounded-lg bg-[#f5f0e8] border border-[#d9d3c5] flex items-center justify-center">
                    <Icon size={18} className="text-[#7c9082]" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-[#2d2a26]">{s.title}</h2>
                    <p className="text-xs text-[#6b665c] font-mono mt-0.5">{s.description}</p>
                  </div>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => { e.preventDefault(); jumpTo(s.id); }}
                    className="text-xs text-[#8a8477] hover:text-[#7c9082] font-mono flex-shrink-0"
                    title="Link to this section"
                  >
                    <LinkIcon size={14} />
                  </a>
                </header>
                <div className="prose prose-sm max-w-none text-[#2d2a26]">
                  {s.body()}
                </div>
              </article>
            );
          })}
          {filteredSections.length === 0 && (
            <div className="bg-white border border-[#d9d3c5] rounded-xl p-12 text-center">
              <MagnifyingGlass size={40} className="text-[#c4bfb4] mx-auto mb-3" />
              <p className="text-sm text-[#6b665c] font-mono">
                No help topics match <strong>"{query}"</strong>. Try a different keyword or clear the search.
              </p>
              <button
                onClick={() => setQuery('')}
                className="mt-4 text-xs text-blue-600 hover:text-blue-700 underline font-mono"
                data-testid="help-clear-search"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Contact footer */}
          <div className="bg-[#f5f0e8] border border-[#d9d3c5] rounded-xl p-5 text-center" data-testid="help-contact">
            <p className="text-sm text-[#2d2a26] mb-1">Still can't find what you're looking for?</p>
            <a
              href="mailto:admin@musicsync.net"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-mono"
            >
              Email admin@musicsync.net <ArrowSquareOut size={14} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
